import { Box, Grid, Skeleton, Stack } from '@mui/material';
import React from 'react';
import i18n from '../../i18n';
import { ACTIVITY_BULLETIN_MAX_SLOTS, BULLETIN_DINING_GUIDE_MAX_SLOTS } from '../../utils/restaurant-static-bundled';
import { SubFunctionAuxiliaryHint } from './sub-function-auxiliary-hint.component';
import './scss/first-screen.page.scss';

type Props = {
    /** 与实页一致：辅助模式时展示说明区 */
    auxiliaryModeEnabled: boolean;
};

/** 推荐菜区骨架占位数量（实页展示上限 6，骨架取 4 个避免过长） */
const SKELETON_RECOMMEND_DISH_SLOTS = 4;

/**
 * 「首页与营销」首包 bundled 未就绪时：按实页栅格占位各白卡区域（顶栏子功能 Tab 不骨架）。
 * 首行与 `first-screen.page` 同：左 480px 基础设置、右满宽顶部文字公告；下列顺序与实页一致。
 */
export function FirstScreenPageSkeleton({ auxiliaryModeEnabled }: Props) {
    return (
        <Box
            className="first-screen-page first-screen-page--skeleton"
            aria-busy="true"
            aria-label={`${i18n.t('first_screen_setting_page_label')}, ${i18n.t('general_text_loading')}`}
        >
            <Box className="first-screen-page__surface common-card-box">
                <SubFunctionAuxiliaryHint
                    enabled={auxiliaryModeEnabled}
                    i18nKey="first_screen_aux_page_intro"
                    className="first-screen-page__aux-intro"
                />
                <Grid container spacing={3} className="first-screen-page__grid" aria-hidden>
                    {/** 首行：与实页 `first-screen-page__top-row` 一致，两列 stretch 等高 */}
                    <Grid item xs={12}>
                        <Box
                            className="first-screen-page__top-row"
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: 3,
                                width: '100%',
                                alignItems: 'stretch',
                            }}
                        >
                            {/** 左列：基础设置卡 — 标题区 + 双 120 图位（与 BackgroundImage 结构接近） */}
                            <Box
                                className="first-screen-page__basic-col"
                                sx={{
                                    width: { xs: '100%', md: '480px' },
                                    flexShrink: { md: 0 },
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: { xs: 240, md: 280 },
                                        borderRadius: '16px',
                                        bgcolor: 'var(--fill-neutral-100)',
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <Skeleton animation="wave" width="45%" height={24} />
                                    <Skeleton animation="wave" width="90%" height={16} sx={{ mt: 0.5 }} />
                                    <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                                        <Skeleton animation="wave" variant="rounded" width={120} height={120} />
                                        <Skeleton animation="wave" variant="rounded" width={120} height={120} />
                                    </Box>
                                </Box>
                            </Box>
                            {/** 右列：顶部文字公告 — 卡头 + 多行列表条（与 Banner 列表区比例接近） */}
                            <Box
                                className="first-screen-page__banner-col"
                                sx={{ flex: { md: '1 1 0%' }, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: { xs: 200, md: 280 },
                                        borderRadius: '16px',
                                        bgcolor: 'var(--fill-neutral-100)',
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Skeleton animation="wave" width="50%" height={24} />
                                        <Skeleton animation="wave" variant="rounded" width={32} height={32} />
                                    </Box>
                                    <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                                        <Skeleton animation="wave" variant="rounded" height={48} />
                                        <Skeleton animation="wave" variant="rounded" height={48} />
                                        <Skeleton animation="wave" variant="rounded" height={48} />
                                        <Skeleton animation="wave" variant="rounded" height={48} sx={{ opacity: 0.6 }} />
                                    </Stack>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    {/** 活动公告图：8 槽位 256×160 量级，大屏约每行 4 个（与 `ActivityBulletin` Grid 一致） */}
                    <Grid item xs={12}>
                        <Box sx={{ borderRadius: '16px', bgcolor: 'var(--fill-neutral-100)', p: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                                <Skeleton animation="wave" width="40%" height={28} />
                                <Skeleton animation="wave" width={120} height={20} />
                            </Box>
                            <Grid container spacing={2}>
                                {Array.from({ length: ACTIVITY_BULLETIN_MAX_SLOTS }).map((_, i) => (
                                    <Grid item key={i}>
                                        <Skeleton animation="wave" variant="rounded" width={256}
                                            height={160} sx={{ borderRadius: '12px' }} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                    {/** 图片就餐指南：5 槽 256×160（与 `BulletinImage` 一致） */}
                    <Grid item xs={12}>
                        <Box sx={{ borderRadius: '16px', bgcolor: 'var(--fill-neutral-100)', p: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                                <Skeleton animation="wave" width="40%" height={28} />
                                <Skeleton animation="wave" width={100} height={20} />
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                    rowGap: 2,
                                }}
                            >
                                {Array.from({ length: BULLETIN_DINING_GUIDE_MAX_SLOTS }).map((_, i) => (
                                    <Skeleton
                                        key={i}
                                        animation="wave"
                                        variant="rounded"
                                        width={256}
                                        height={160}
                                        sx={{
                                            borderRadius: '12px',
                                            flex: '1 1 200px',
                                            maxWidth: '100%',
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                    {/** 推荐菜品：卡头 + 多图位（与 `SpecialDishes` 256×160 一致） */}
                    <Grid item xs={12}>
                        <Box sx={{ borderRadius: '16px', bgcolor: 'var(--fill-neutral-100)', p: 2 }}>
                            <Box sx={{ mb: 2 }}>
                                <Skeleton animation="wave" width="55%" height={28} />
                                <Skeleton animation="wave" width="70%" height={16} sx={{ mt: 0.5 }} />
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {Array.from({ length: SKELETON_RECOMMEND_DISH_SLOTS }).map((_, i) => (
                                    <Skeleton
                                        key={i}
                                        animation="wave"
                                        variant="rounded"
                                        width={256}
                                        height={160}
                                        sx={{
                                            borderRadius: '12px',
                                            flex: '0 0 auto',
                                            maxWidth: '100%',
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                    {/** 文字就餐指南 + 社媒：与实页同排双列、卡片等高（`first-screen-page__guide-propaganda-row`） */}
                    <Grid item xs={12}>
                        <Grid container spacing={3} className="first-screen-page__guide-propaganda-row">
                            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            minHeight: 200,
                                            borderRadius: '16px',
                                            bgcolor: 'var(--fill-neutral-100)',
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Skeleton animation="wave" width="42%" height={24} />
                                        <Skeleton animation="wave" width="85%" height={16} sx={{ mt: 1.5 }} />
                                        <Skeleton animation="wave" variant="rounded" height={100} sx={{ borderRadius: '8px', mt: 'auto' }} />
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            minHeight: 200,
                                            borderRadius: '16px',
                                            bgcolor: 'var(--fill-neutral-100)',
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Skeleton animation="wave" width="42%" height={24} />
                                        <Stack spacing={1} sx={{ mt: 1.5, flex: 1 }}>
                                            <Skeleton animation="wave" variant="rounded" height={40} />
                                            <Skeleton animation="wave" variant="rounded" height={40} />
                                            <Skeleton animation="wave" variant="rounded" height={40} sx={{ opacity: 0.7 }} />
                                        </Stack>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
